import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { Plus, DollarSign, TrendingUp, FileText, Download, Check, X, Upload, Image, Loader2, Trash2 } from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function ExpensesManagement() {
  const [expenses, setExpenses] = useState([]);
  const [expenseReport, setExpenseReport] = useState(null);
  const [handymen, setHandymen] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const [formData, setFormData] = useState({
    expense_type: 'materials',
    category: '',
    amount: '',
    description: '',
    job_id: '',
    handyman_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expensesRes, reportRes, handymenRes, jobsRes] = await Promise.all([
        axiosInstance.get('/expenses'),
        axiosInstance.get('/reports/expenses'),
        axiosInstance.get('/handymen'),
        axiosInstance.get('/jobs')
      ]);
      setExpenses(expensesRes.data);
      setExpenseReport(reportRes.data);
      setHandymen(handymenRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/expenses', {
        ...formData,
        amount: parseFloat(formData.amount),
        attachments
      });
      toast.success('Expense created successfully');
      setShowAddDialog(false);
      setFormData({ expense_type: 'materials', category: '', amount: '', description: '', job_id: '', handyman_id: '' });
      setAttachments([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to create expense');
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingFiles(true);
    try {
      const formPayload = new FormData();
      for (let i = 0; i < files.length; i++) {
        formPayload.append('files', files[i]);
      }
      formPayload.append('folder', '/expenses');
      const res = await axiosInstance.post('/upload-multiple', formPayload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploaded = (res.data.files || []).map(f => ({
        name: f.name || f.file_name,
        url: f.url,
        file_id: f.file_id,
        type: f.name?.match(/\.(pdf|doc|docx)$/i) ? 'document' : 'image'
      }));
      setAttachments(prev => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploadingFiles(false);
      e.target.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateExpenseStatus = async (expenseId, status, reason = '') => {
    try {
      await axiosInstance.patch(`/expenses/${expenseId}`, {
        status,
        approved_by: 'admin',
        rejection_reason: reason
      });
      toast.success(`Expense ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update expense');
    }
  };

  const exportToCSV = () => {
    if (!expenses || expenses.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Date', 'Handyman', 'Category', 'Type', 'Amount', 'Status', 'Description'];
    const rows = expenses.map(exp => [
      formatDate(exp.submitted_date),
      exp.handyman_name || 'Admin',
      exp.category,
      exp.expense_type,
      exp.amount,
      exp.status,
      exp.description
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expenses_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Report exported successfully');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      approved: 'bg-green-500',
      rejected: 'bg-red-500',
      paid: 'bg-blue-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  return (
    <div data-testid="expenses-management">
      <div className="mb-8">
        <h2 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Expenses Management
        </h2>
        <p className="text-gray-600">Track and manage business expenses</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#595959]"></div>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Total Expenses</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold" data-testid="total-expenses">
                  ${expenseReport?.total_expenses?.toFixed(2) || '0.00'}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Approved</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold" data-testid="approved-expenses">
                  ${expenseReport?.approved_expenses?.toFixed(2) || '0.00'}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white border-0 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Pending</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold" data-testid="pending-expenses">
                  ${expenseReport?.pending_expenses?.toFixed(2) || '0.00'}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Total Claims</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold" data-testid="total-claims">
                  {expenseReport?.total_claims || 0}
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="expenses" className="w-full">
            <div className="flex items-center justify-between mb-4">
              <TabsList>
                <TabsTrigger value="expenses" data-testid="expenses-tab">All Expenses</TabsTrigger>
                <TabsTrigger value="reports" data-testid="reports-tab">Reports</TabsTrigger>
              </TabsList>
              <div className="flex gap-2">
                <Button variant="outline" onClick={exportToCSV} data-testid="export-expenses-btn">
                  <Download size={16} className="mr-2" />
                  Export CSV
                </Button>
                <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-expense-btn">
                      <Plus size={16} className="mr-2" />
                      Add Expense
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Expense</DialogTitle>
                      <DialogDescription>Record a new business expense</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateExpense} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Expense Type</Label>
                        <Select value={formData.expense_type} onValueChange={(value) => setFormData({...formData, expense_type: value})}>
                          <SelectTrigger data-testid="expense-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="travel">Travel</SelectItem>
                            <SelectItem value="materials">Materials</SelectItem>
                            <SelectItem value="equipment">Equipment</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Input
                          value={formData.category}
                          onChange={(e) => setFormData({...formData, category: e.target.value})}
                          placeholder="e.g., Fuel, Tools"
                          data-testid="expense-category-input"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Amount ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.amount}
                          onChange={(e) => setFormData({...formData, amount: e.target.value})}
                          placeholder="0.00"
                          data-testid="expense-amount-input"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Handyman (Optional)</Label>
                        <Select value={formData.handyman_id || "none"} onValueChange={(value) => setFormData({...formData, handyman_id: value === "none" ? "" : value})}>
                          <SelectTrigger data-testid="expense-handyman-select">
                            <SelectValue placeholder="Select handyman" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None (Admin Expense)</SelectItem>
                            {handymen.map((handyman) => (
                              <SelectItem key={handyman.id} value={handyman.id}>
                                {handyman.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Job (Optional)</Label>
                        <Select value={formData.job_id || "none"} onValueChange={(value) => setFormData({...formData, job_id: value === "none" ? "" : value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select job" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {jobs.slice(0, 20).map((job) => (
                              <SelectItem key={job.id} value={job.id}>
                                {job.service_name} - {job.customer_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          value={formData.description}
                          onChange={(e) => setFormData({...formData, description: e.target.value})}
                          placeholder="Expense details..."
                          data-testid="expense-description-input"
                          required
                        />
                      </div>

                      {/* File Upload */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1.5">
                          <Upload size={14} />
                          Attachments (Receipts, Photos, PDFs)
                        </Label>
                        <div className="flex items-center gap-2">
                          <label className="flex-1 cursor-pointer">
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-gray-400 transition-colors">
                              {uploadingFiles ? (
                                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                                  <Loader2 size={16} className="animate-spin" />
                                  Uploading...
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500">
                                  <Upload size={16} className="mx-auto mb-1" />
                                  Click to upload images or documents
                                </div>
                              )}
                            </div>
                            <input
                              type="file"
                              multiple
                              accept="image/*,.pdf,.doc,.docx"
                              className="hidden"
                              onChange={handleFileUpload}
                              disabled={uploadingFiles}
                              data-testid="expense-file-input"
                            />
                          </label>
                        </div>
                        {attachments.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {attachments.map((file, idx) => (
                              <div key={idx} className="flex items-center gap-1.5 bg-gray-100 rounded-lg px-2 py-1 text-xs">
                                {file.type === 'image' ? <Image size={12} className="text-blue-500" /> : <FileText size={12} className="text-red-500" />}
                                <span className="max-w-[120px] truncate">{file.name}</span>
                                <button type="button" onClick={() => removeAttachment(idx)} className="text-gray-400 hover:text-red-500">
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <Button type="submit" className="w-full" data-testid="submit-expense-btn">
                        Create Expense
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <TabsContent value="expenses" className="space-y-4">
              {expenses.map((expense) => (
                <Card key={expense.id} className="backdrop-blur-sm bg-white/80 shadow-lg border-0" data-testid={`expense-card-${expense.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold">{expense.category}</h3>
                        <p className="text-sm text-gray-500">
                          {expense.handyman_name || 'Admin'} | {expense.expense_type}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900">${(expense.amount || 0).toFixed(2)}</div>
                        <Badge className={`${getStatusColor(expense.status)} text-white mt-2`}>
                          {expense.status.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">{expense.description}</p>
                    {expense.attachments?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {expense.attachments.map((file, idx) => (
                          <a
                            key={idx}
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg px-2.5 py-1.5 text-xs transition-colors"
                          >
                            {file.type === 'image' ? <Image size={12} className="text-blue-500" /> : <FileText size={12} className="text-red-500" />}
                            <span className="max-w-[100px] truncate">{file.name}</span>
                          </a>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="text-sm text-gray-500">
                        {formatDate(expense.submitted_date)}
                      </div>
                      {expense.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleUpdateExpenseStatus(expense.id, 'approved')}
                            data-testid={`approve-expense-${expense.id}`}
                          >
                            <Check size={14} className="mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateExpenseStatus(expense.id, 'rejected', 'Not approved')}
                            data-testid={`reject-expense-${expense.id}`}
                          >
                            <X size={14} className="mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="reports">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* By Category */}
                <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
                  <CardHeader>
                    <CardTitle>Expenses by Category</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {expenseReport?.by_category && Object.entries(expenseReport.by_category).map(([category, data]) => (
                        <div key={category} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <div>
                            <div className="font-medium">{category}</div>
                            <div className="text-sm text-gray-500">{data.count} claims</div>
                          </div>
                          <div className="text-lg font-bold">${(data.total || 0).toFixed(2)}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* By Handyman */}
                <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
                  <CardHeader>
                    <CardTitle>Expenses by Handyman</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {expenseReport?.by_handyman && Object.entries(expenseReport.by_handyman).map(([name, data]) => (
                        <div key={name} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <div>
                            <div className="font-medium">{name}</div>
                            <div className="text-sm text-gray-500">{data.count} claims</div>
                          </div>
                          <div className="text-lg font-bold">${(data.total || 0).toFixed(2)}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
